import React, { useEffect } from "react";
import { useLeaveRequest } from "../../../../api/queries/leaveQueries";
import { getApiErrorMessage } from "../../../../utils/apiError";
import { formatDateTime, fromNow } from "../../../../utils/dateTime";
import { employeeName } from "../../../../utils/text";
import LeaveStatusBadge from "./LeaveStatusBadge";
import { formatDays, formatLeaveDates, num } from "./leaveFormat";
import ApprovalTrail, {
  LevelBadge,
} from "../../../approvals/components/ApprovalTrail";
import "../../../approvals/approvals.css";

const Meta = ({ label, children }) => (
  <div>
    <div className="approval-meta-label">{label}</div>
    <div className="leave-meta-value">{children || "—"}</div>
  </div>
);

/** Allocated / Used / Pending / Available for this leave type and year. */
const BalancePanel = ({ request, balance }) => {
  if (!balance) {
    return (
      <div className="alert alert-danger fs-085 d-flex gap-2 m-0" role="alert">
        <i className="bi bi-exclamation-triangle-fill mt-1"></i>
        <div>
          <strong>No {request.leaveType?.name || "leave"} balance found</strong>{" "}
          for this employee and year. Check their allocation before approving.
        </div>
      </div>
    );
  }
  const available = num(balance.available);
  const isNegative = available < 0;
  return (
    <>
      <div className="leave-balance-grid">
        {[
          ["Allocated", balance.allocated],
          ["Used", balance.used],
          ["Pending", balance.pending],
        ].map(([label, value]) => (
          <div key={label} className="leave-balance-cell">
            <div className="approval-meta-label">{label}</div>
            <div className="leave-balance-value">{num(value)}</div>
          </div>
        ))}
        <div
          className={`leave-balance-cell leave-balance-cell--main ${isNegative ? "is-negative" : ""}`}
        >
          <div className="approval-meta-label">Available</div>
          <div className="leave-balance-value">{available}</div>
        </div>
      </div>
      {request.status === "pending" && !isNegative && (
        // Pending days are already deducted from `available`
        <p className="fs-085 text-slate-600 mt-2 mb-0">
          After approval:{" "}
          <strong className="text-navy">{formatDays(available)}</strong> left.
        </p>
      )}
      {isNegative && (
        <div
          className="alert alert-danger fs-085 d-flex gap-2 mt-2 mb-0"
          role="alert"
        >
          <i className="bi bi-exclamation-triangle-fill mt-1"></i>
          <div>
            Not enough balance: this leave puts the employee{" "}
            {formatDays(Math.abs(available))} over.
          </div>
        </div>
      )}
    </>
  );
};

const ReviewHistory = ({ request }) => {
  if (!request.reviewedAt && !request.cancelledAt) {
    return <p className="fs-085 text-slate-500 mb-0">Not reviewed yet.</p>;
  }
  return (
    <ul className="leave-history">
      {request.reviewedAt && (
        <li>
          <div className="fs-085">
            {/* A cancelled request with a review date was approved first */}
            <strong>
              {request.status === "rejected" ? "Rejected" : "Approved"}
            </strong>
            <span className="text-slate-500">
              {" "}
              · {formatDateTime(request.reviewedAt)}
            </span>
          </div>
          {request.reviewRemarks && (
            <div
              className={`approval-note mt-1 ${request.status === "rejected" ? "approval-note--rejected" : "approval-note--review"}`}
            >
              {request.reviewRemarks}
            </div>
          )}
        </li>
      )}
      {request.cancelledAt && (
        <li>
          <div className="fs-085">
            <strong>Cancelled</strong>
            <span className="text-slate-500">
              {" "}
              · {formatDateTime(request.cancelledAt)}
            </span>
          </div>
        </li>
      )}
    </ul>
  );
};

/** Approvers per level (any one person of a level may act). */
const ApproverLevels = ({ approvers = [], levels }) => {
  const byLevel = approvers.reduce(
    (m, a) => m.set(a.level, [...(m.get(a.level) || []), a]),
    new Map(),
  );
  if (byLevel.size === 0)
    return (
      <p className="fs-085 text-slate-500 mb-0">
        Branch manager / HR (default).
      </p>
    );
  return (
    <ol className="leave-approver-levels">
      {[...byLevel.entries()]
        .sort(([a], [b]) => a - b)
        .map(([level, people]) => (
          <li
            key={level}
            className={levels?.current === level ? "is-current" : ""}
          >
            <span className="text-slate-500 me-1">
              Level {level}
              {levels?.current === level ? " (now)" : ""}:
            </span>
            {people.map((p, i) => (
              <span key={`${p.employeeId || i}`}>
                {i > 0 && <span className="text-slate-400"> or </span>}
                <strong>{p.name}</strong>
                {p.source && (
                  <span className="text-slate-500 fs-070">
                    {" "}
                    · {p.source === "EMPLOYEE" ? "own chain" : "rule"}
                  </span>
                )}
              </span>
            ))}
          </li>
        ))}
    </ol>
  );
};

/**
 * Side drawer with one leave request: details, balance, review history,
 * and Approve / Reject (when canReview) or Cancel (approved leave).
 * onAction(type, request) opens the matching dialog; `paused` = a dialog is
 * open on top, so Escape belongs to it.
 */
const LeaveRequestDrawer = ({
  id,
  onClose,
  onAction,
  isForbidden,
  paused,
  readOnly,
}) => {
  const query = useLeaveRequest(id);
  const { request: rawRequest, balance, canReview } = query.data || {};
  // Multi-level routing: { request, balance, approvers, levels, canReview }
  const levels = query.data?.levels;
  const approvers = query.data?.approvers || [];
  const request = rawRequest ? { ...rawRequest, levels } : rawRequest;

  useEffect(() => {
    if (paused) return undefined;
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose, paused]);

  const blocked = isForbidden?.(id) || readOnly; // readOnly = role can view leave but not change it
  const showReview = request?.status === "pending" && canReview && !blocked;
  const showCancel = request?.status === "approved" && !blocked;

  return (
    <div className="app-drawer-backdrop" onClick={onClose}>
      <aside
        className="app-drawer d-flex flex-column"
        role="dialog"
        aria-modal="true"
        aria-labelledby="leave-drawer-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="app-dialog-header">
          <div>
            <h5 id="leave-drawer-title" className="fw-bold text-navy m-0">
              {request ? employeeName(request.employee) : "Leave request"}
            </h5>
            {request && (
              <div className="d-flex align-items-center gap-2 mt-1">
                <span className="text-slate-500 fs-080">
                  {request.employee?.employeeId}
                </span>
                <LeaveStatusBadge status={request.status} />
                {request.status === "pending" && (
                  <LevelBadge levels={levels} status="PENDING" />
                )}
              </div>
            )}
          </div>
          <button
            type="button"
            className="btn-close"
            aria-label="Close"
            onClick={onClose}
          ></button>
        </div>

        <div className="app-drawer-body">
          {query.isLoading && (
            <div className="text-center py-5">
              <span
                className="spinner-border text-primary"
                role="status"
                aria-label="Loading"
              ></span>
            </div>
          )}
          {query.isError && (
            <div className="alert alert-danger fs-085" role="alert">
              {getApiErrorMessage(query.error, "Could not load this request.")}
            </div>
          )}

          {request && (
            <div className="d-flex flex-column gap-4">
              <section>
                <h6 className="leave-section-title">Request</h6>
                <div className="leave-meta-grid">
                  <Meta label="Leave type">{request.leaveType?.name}</Meta>
                  <Meta label="Days">{formatDays(request.totalDays)}</Meta>
                  <Meta label="Dates">
                    {formatLeaveDates(request.startDate, request.endDate)}
                  </Meta>
                  <Meta label="Filed">
                    <span title={formatDateTime(request.createdAt)}>
                      {fromNow(request.createdAt)}
                    </span>
                  </Meta>
                  <Meta label="Branch">
                    {request.employee?.branch?.branchName}
                  </Meta>
                  <Meta label="Department">
                    {request.employee?.department?.departmentName}
                  </Meta>
                </div>
                <div className="approval-meta-label mt-3">Reason</div>
                <div className="approval-note">{request.reason || "—"}</div>
              </section>

              <section>
                <h6 className="leave-section-title">
                  Balance{balance?.year ? ` · ${balance.year}` : ""}
                </h6>
                <BalancePanel request={request} balance={balance} />
              </section>

              {(approvers.length > 0 || levels?.total > 1) && (
                <section>
                  <h6 className="leave-section-title">Approvers</h6>
                  <ApproverLevels approvers={approvers} levels={levels} />
                </section>
              )}

              <section>
                <h6 className="leave-section-title">Review history</h6>
                {(request.approvals || []).length > 0 ? (
                  <ApprovalTrail
                    approvals={request.approvals}
                    levels={levels}
                  />
                ) : (
                  <ReviewHistory request={request} />
                )}
              </section>

              {request.status === "pending" && !canReview && (
                <p className="fs-080 text-slate-500 mb-0">
                  <i className="bi bi-info-circle me-1"></i>
                  {levels?.total > 1
                    ? `Waiting for the level ${levels.current} approver.`
                    : "You can view this request but not approve or reject it."}
                </p>
              )}
            </div>
          )}
        </div>

        {(showReview || showCancel) && (
          <div className="app-dialog-footer">
            {showCancel && (
              <button
                type="button"
                className="btn btn-outline-warning profile-btn"
                onClick={() => onAction("cancel", request)}
              >
                <i className="bi bi-x-circle"></i> Cancel leave
              </button>
            )}
            {showReview && (
              <>
                <button
                  type="button"
                  className="btn btn-outline-danger profile-btn"
                  onClick={() => onAction("reject", request)}
                >
                  <i className="bi bi-x-lg"></i> Reject
                </button>
                <button
                  type="button"
                  className="btn btn-success profile-btn"
                  onClick={() => onAction("approve", request)}
                >
                  <i className="bi bi-check-lg"></i> Approve
                </button>
              </>
            )}
          </div>
        )}
      </aside>
    </div>
  );
};

export default LeaveRequestDrawer;
